import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, Users, ArrowLeft, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthParam = () => {
  const token = localStorage.getItem('c3i_token');
  return token ? `Bearer ${token}` : '';
};

const getAuthHeader = () => {
  const token = localStorage.getItem('c3i_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export default function SegnalazioniGTLPage() {
  const { user, isAdminOrdine } = useAuth();
  const navigate = useNavigate();
  
  const [gtlList, setGtlList] = useState([]);
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    cellulare: '',
    gtl_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  useEffect(() => {
    if (!isAdminOrdine()) {
      navigate('/dashboard');
      return;
    }
    fetchGTLList();
  }, [isAdminOrdine, navigate]);

  const fetchGTLList = async () => {
    try {
      const response = await axios.get(`${API}/public/gtl`);
      // Filter only active GTLs
      const activeGTLs = response.data.filter(g => g.stato === 'attivo');
      setGtlList(activeGTLs);
    } catch (error) {
      console.error('Error fetching GTL list:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nome || !formData.cognome || !formData.email || !formData.cellulare || !formData.gtl_id) {
      toast.error('Tutti i campi sono obbligatori');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/segnalazioni-gtl?Authorization=${encodeURIComponent(getAuthParam())}`,
        formData
      );
      
      const selectedGTL = gtlList.find(g => g.id === formData.gtl_id);
      setSubmittedData({
        gtl: selectedGTL?.nome || response.data.gtl || 'GTL',
        nome: formData.nome,
        cognome: formData.cognome,
        richiesta_id: response.data.richiesta_id
      });
      setSubmitted(true);
      toast.success('Richiesta di inserimento generata correttamente');
    } catch (error) {
      console.error('Error submitting segnalazione:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio della segnalazione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!submittedData?.richiesta_id) return;
    
    try {
      const token = localStorage.getItem('c3i_token');
      const response = await fetch(
        `${API}/segnalazioni-gtl/${submittedData.richiesta_id}/pdf?Authorization=${encodeURIComponent(`Bearer ${token}`)}`,
        {
          method: 'GET',
          headers: getAuthHeader()
        }
      );
      
      if (!response.ok) {
        throw new Error('Errore durante il download del PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Segnalazione_GTL_${submittedData.cognome}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Errore durante il download del PDF');
    }
  };

  // Success page after submission
  if (submitted) {
    return (
      <div className="p-6 lg:p-8" data-testid="segnalazione-success-page">
        <div className="max-w-xl mx-auto">
          <Card className="card-institutional">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                Richiesta di inserimento generata correttamente
              </h2>
              <p className="text-slate-600 mb-6">
                La richiesta deve essere ora scaricata, firmata digitalmente e inviata via PEC a{' '}
                <a href="mailto:segreteria@ingpec.eu" className="text-blue-600 hover:underline">
                  segreteria@ingpec.eu
                </a>
              </p>
              {submittedData && (
                <div className="p-4 bg-slate-50 rounded-lg mb-6 text-left">
                  <p className="text-sm text-slate-600">
                    <strong>GTL:</strong> {submittedData.gtl}
                  </p>
                  <p className="text-sm text-slate-600">
                    <strong>Segnalato:</strong> {submittedData.nome} {submittedData.cognome}
                  </p>
                </div>
              )}
              
              {/* Download PDF Button */}
              <Button 
                onClick={handleDownloadPdf} 
                variant="outline" 
                className="w-full mb-4"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Scarica PDF
              </Button>
              
              <Button onClick={() => navigate('/dashboard')} className="w-full" data-testid="back-to-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna alla Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="segnalazioni-gtl-page">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Dashboard
          </Button>
        </div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-4">Segnalazioni per GTL</h1>
        <p className="text-slate-500 mt-1">Invia una richiesta di inserimento in un gruppo di lavoro</p>
      </div>

      <div className="max-w-xl mx-auto">
        <Card className="card-institutional">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-600" />
              Dati del Segnalato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => handleChange('nome', e.target.value)}
                  placeholder="Mario"
                  data-testid="input-nome"
                />
              </div>

              {/* Cognome */}
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome *</Label>
                <Input
                  id="cognome"
                  value={formData.cognome}
                  onChange={(e) => handleChange('cognome', e.target.value)}
                  placeholder="Rossi"
                  data-testid="input-cognome"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="mario.rossi@email.it"
                  data-testid="input-email"
                />
              </div>

              {/* Cellulare */}
              <div className="space-y-2">
                <Label htmlFor="cellulare">Cellulare *</Label>
                <Input
                  id="cellulare"
                  type="tel"
                  value={formData.cellulare}
                  onChange={(e) => handleChange('cellulare', e.target.value)}
                  placeholder="+39 333 1234567"
                  data-testid="input-cellulare"
                />
              </div>

              {/* GTL Selection */}
              <div className="space-y-2">
                <Label htmlFor="gtl">Gruppo di lavoro *</Label>
                <Select 
                  value={formData.gtl_id} 
                  onValueChange={(v) => handleChange('gtl_id', v)}
                >
                  <SelectTrigger data-testid="select-gtl">
                    <SelectValue placeholder="Seleziona un GTL..." />
                  </SelectTrigger>
                  <SelectContent>
                    {gtlList.map(gtl => (
                      <SelectItem key={gtl.id} value={gtl.id}>{gtl.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={submitting}
                data-testid="submit-segnalazione-btn"
              >
                {submitting ? 'Invio in corso...' : 'INVIO'}
              </Button>

              <p className="text-xs text-slate-500 text-center">* Campi obbligatori</p>
            </form>

            {/* Nota */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>NOTA</strong> - Si ricorda che il Delegato C3i dell'Ordine può iscriversi autonomamente ai GTL. 
                Pertanto, questo modulo deve essere utilizzato esclusivamente per segnalare altri iscritti dell'Ordine 
                interessati a far parte dei GTL. Le richieste verranno esaminate nel primo Consiglio C3i utile; 
                in caso di approvazione, la segreteria del CNI invierà comunicazione ufficiale dell'inserimento.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
